import type { Editor } from '@tiptap/react';
import { liftEmptyBlock } from 'prosemirror-commands';
import { TextSelection } from 'prosemirror-state';

/**
 * Handle backspace key for blockquote with proper empty line handling
 */
export const handleBlockquoteBackspace = (editor: Editor, event: KeyboardEvent): boolean => {
  if (event.key !== 'Backspace') {
    return false;
  }

  const { state, view } = editor;
  const { dispatch } = view;
  const { empty, $head: selectionHeadPosition } = state.selection;

  if (!empty) {
    return false;
  }

  const paragraphNode = selectionHeadPosition.parent;
  const isCaretAtParagraphStart = selectionHeadPosition.parentOffset === 0;
  const isParagraphCompletelyEmpty = paragraphNode.content.size === 0;
  const isEmptyParagraph =
    isCaretAtParagraphStart &&
    paragraphNode.type.name === 'paragraph' &&
    isParagraphCompletelyEmpty;

  if (!isEmptyParagraph) {
    return false;
  }

  if (
    selectionHeadPosition.depth > 1 &&
    selectionHeadPosition.node(selectionHeadPosition.depth - 1).type.name === 'blockquote'
  ) {
    event.preventDefault();

    if (!liftEmptyBlock(state, dispatch)) {
      return false;
    }

    const { $from: currentSelectionStart } = state.selection;
    const blockquotePosition = currentSelectionStart.before(currentSelectionStart.depth - 1);
    const blockquoteNode = state.doc.nodeAt(blockquotePosition);

    if (
      blockquoteNode &&
      blockquoteNode.type.name === 'blockquote' &&
      blockquoteNode.childCount === 0
    ) {
      dispatch(state.tr.delete(blockquotePosition, blockquotePosition + blockquoteNode.nodeSize));
    }

    return true;
  }

  event.preventDefault();

  const paragraphStartPosition = selectionHeadPosition.before();
  const paragraphEndPosition = selectionHeadPosition.after();
  let transaction = state.tr.delete(paragraphStartPosition, paragraphEndPosition);

  const positionBeforeDeletedParagraph = Math.max(0, paragraphStartPosition - 1);
  const resolvedPosition = transaction.doc.resolve(positionBeforeDeletedParagraph);
  const textSelectionAfterDelete = TextSelection.near(resolvedPosition, -1);
  transaction = transaction.setSelection(textSelectionAfterDelete);

  dispatch(transaction);
  return true;
};
